import { Controller } from "@hotwired/stimulus"

// Live net wheel load per reading row. The correction is certificate data:
// the selected scale option carries its correction points as JSON
// [[test_load_kg, correction_kg], ...] and each row resolves the point
// nearest to its display value (tie -> lower load), mirroring
// Scale#correction_for. Scoped to the whole scale-usage block so the scale
// select and every reading row share one controller.
export default class extends Controller {
  static targets = ["scale", "display", "tare", "net", "formula", "correction"]

  connect() {
    this.sync()
  }

  sync() {
    const points = this.correctionPoints()
    this.displayTargets.forEach((displayInput, index) => {
      const tare = this.number(this.tareTargets[index]?.value)
      const net = this.netTargets[index]
      const formula = this.formulaTargets[index]
      const correctionOutput = this.correctionTargets[index]
      if (!net || !formula) return

      if (displayInput.value === "") {
        net.textContent = "— kg"
        formula.textContent = "Enter the scale display"
        if (correctionOutput) correctionOutput.textContent = "— kg"
        return
      }

      const display = this.number(displayInput.value)
      const correction = this.correctionFor(points, display)
      const value = display - tare + correction
      net.textContent = `${this.format(value)} kg`
      // "—" means the selected scale carries no certificate data at all —
      // distinct from a certificate that says the correction is 0.
      if (correctionOutput) {
        correctionOutput.textContent = points.length ? `${this.formatSigned(correction)} kg` : "— kg"
      }
      formula.textContent = correction === 0
        ? `${this.format(display)} − ${this.format(tare)} tare`
        : `${this.format(display)} − ${this.format(tare)} tare ${correction > 0 ? "+" : "−"} ${this.format(Math.abs(correction))} certificate correction`
    })
  }

  correctionPoints() {
    if (!this.hasScaleTarget) return []
    const option = this.scaleTarget.selectedOptions[0]
    if (!option || !option.dataset.correctionPoints) return []
    try {
      return JSON.parse(option.dataset.correctionPoints)
    } catch {
      return []
    }
  }

  correctionFor(points, display) {
    if (!points.length || isNaN(display)) return 0
    let best = null
    for (const [load, correction] of points) {
      const distance = Math.abs(load - display)
      if (best === null || distance < best.distance || (distance === best.distance && load < best.load)) {
        best = { distance, load, correction }
      }
    }
    return best.correction
  }

  number(value) {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? 0 : parsed
  }

  format(value) {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, "")
  }

  // Mirrors ApplicationHelper#signed_kg (ASCII sign, two decimals max) so the
  // server-rendered cell and the live one read identically.
  formatSigned(value) {
    const rounded = Math.round(value * 100) / 100
    if (rounded === 0) return "0"
    return `${rounded > 0 ? "+" : "-"}${this.format(Math.abs(rounded))}`
  }
}
